import { useQuery } from "@tanstack/react-query";

import {
  ClientRatingScopeType,
  GLOBAL_CLIENT_RATING_SCOPE_ID,
  type ClientRatingSnapshot,
} from "@fitness-app/data-models/entities/ClientRating";
import { type TraineeProfile } from "@fitness-app/data-models/entities/Trainee";
import { type TraineeSettings } from "@fitness-app/data-models/entities/TraineeSettings";
import {
  type ClientTrainingProgram,
  type ClientTrainingProgramDetails,
} from "@fitness-app/data-models/entities/TrainingProgram";
import { type UserStatus } from "@fitness-app/data-models/entities/UserStatus";

import { supabase } from "~/store/initializeStore";
import { useTraineeId } from "./TraineeScope";
import { traineeKeys } from "./traineeKeys";

// Ten sam select co `fetchTrainee({ id })` w app-store, żeby komponenty dostały ten sam kształt profilu.
const TRAINEE_PROFILE_SELECT =
  "*, assignedTrainer:assignedTrainerId(id, firstName, lastName, userId, email, profile:user_profile!team_member_userId_fkey(avatarUrl)), productClient:productId(product(name), productId, status, accessPeriod, productDuration, phoneNumber, scheduledAccessPeriod), user:userId(id, firstName, lastName, avatarUrl), nutrition:activeMealsPlanId(name, id, startAt)";

export const fetchTraineeProfile = async (traineeId: string) => {
  const { data, error } = await supabase
    .from("trainee")
    .select(TRAINEE_PROFILE_SELECT)
    .eq("id", traineeId)
    .single<TraineeProfile>();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const useTraineeProfileById = (traineeId: string) =>
  useQuery({
    queryKey: traineeKeys.profile(traineeId),
    queryFn: () => fetchTraineeProfile(traineeId),
    // Zmiany profilu odświeża realtime karty (`useTraineeRealtime`). Bez tego każdy komponent
    // zamontowany chwilę po pierwszym pobraniu (leniwie ładowane zakładki) pobierał profil od nowa.
    staleTime: 30_000,
  });

export const useTraineeProfile = () => useTraineeProfileById(useTraineeId());

export const useTraineeSettings = () => {
  const traineeId = useTraineeId();

  return useQuery({
    queryKey: traineeKeys.settings(traineeId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trainee_settings")
        .select("*")
        .eq("id", traineeId)
        .single<TraineeSettings>();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
  });
};

export const useTraineeUserStatus = () => {
  const traineeId = useTraineeId();
  const userId = useTraineeProfile().data?.userId;

  return useQuery({
    queryKey: [...traineeKeys.userStatus(traineeId), userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_status")
        .select("*")
        .eq("id", userId!)
        .maybeSingle<UserStatus>();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
  });
};

export const useTraineeRating = () => {
  const traineeId = useTraineeId();

  return useQuery({
    queryKey: traineeKeys.rating(traineeId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_rating_snapshot")
        .select("*")
        .eq("traineeId", traineeId)
        .eq("scopeType", ClientRatingScopeType.Global)
        .eq("scopeId", GLOBAL_CLIENT_RATING_SCOPE_ID)
        .maybeSingle<ClientRatingSnapshot>();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
  });
};

export interface TraineeProgramWithDetails {
  plan: ClientTrainingProgram;
  details: ClientTrainingProgramDetails | null;
  additionalDetails: ClientTrainingProgramDetails[];
}

/** Aktywny plan treningowy klientki (to samo co `fetchProgramWithDetails`, ale per karta). */
export const useTraineeActiveProgram = () => {
  const traineeId = useTraineeId();
  const programId = useTraineeProfile().data?.activeTrainingProgramId;

  return useQuery({
    queryKey: traineeKeys.program(traineeId, programId),
    enabled: Boolean(programId),
    queryFn: async (): Promise<TraineeProgramWithDetails> => {
      const { data, error } = await supabase
        .from("client_training_program")
        .select("*, details:client_training_program_details (*)")
        .eq("id", programId!)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      const { details, ...plan } = data;
      const detailsList = (
        Array.isArray(details) ? details : details ? [details] : []
      ) as ClientTrainingProgramDetails[];

      return {
        plan: plan as ClientTrainingProgram,
        details: detailsList[0] ?? null,
        additionalDetails: detailsList.slice(1),
      };
    },
  });
};
